import { useState, useCallback } from "react";
import StaffLayout from "@/components/StaffLayout";
import {
  useListStatuses,
  useCreateStatus,
  useUpdateStatus,
  useReorderStatuses,
  getListStatusesQueryKey,
  Status,
  ApiError,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Edit, GripVertical, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const QUERY_KEY = getListStatusesQueryKey();

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function ScopeBadges({ s }: { s: Status }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {s.appliesToReports && (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          Reports
        </Badge>
      )}
      {s.appliesToIssues && (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
          Issues
        </Badge>
      )}
    </div>
  );
}

function SortableRow({
  status,
  onEdit,
  onToggleActive,
  isToggling,
}: {
  status: Status;
  onEdit: (s: Status) => void;
  onToggleActive: (s: Status) => void;
  isToggling: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? ("relative" as const) : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={!status.isActive ? "opacity-60" : undefined}>
      <TableCell>
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none p-1"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium text-slate-900 dark:text-white">
        {status.label}
      </TableCell>
      <TableCell className="text-xs text-slate-500 font-mono">{status.key}</TableCell>
      <TableCell>
        <ScopeBadges s={status} />
      </TableCell>
      <TableCell>
        <Switch
          checked={status.isActive}
          onCheckedChange={() => onToggleActive(status)}
          disabled={isToggling}
          aria-label={status.isActive ? "Deactivate" : "Activate"}
        />
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(status)}
          className="text-slate-500 hover:text-primary"
        >
          <Edit className="w-4 h-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default function StaffStatusesPage() {
  const { data: serverStatuses, isLoading, isError } = useListStatuses();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createStatus = useCreateStatus();
  const updateStatus = useUpdateStatus();
  // Optimistic drag reorder. The query cache is the only source of truth for
  // the list. These callbacks live on the hook rather than on mutate(), so
  // they run for every request, even if the page unmounts before it settles.
  const reorderStatuses = useReorderStatuses({
    mutation: {
      onMutate: async ({ data }) => {
        // Cancel any in-flight read first, or it can land after the
        // optimistic write and put the old order back.
        await queryClient.cancelQueries({ queryKey: QUERY_KEY });
        const snapshot = queryClient.getQueryData<Status[]>(QUERY_KEY);
        const sortOrderById = new Map(data.items.map((item) => [item.id, item.sortOrder]));
        queryClient.setQueryData<Status[]>(QUERY_KEY, (prev) =>
          prev
            ?.map((s) => ({ ...s, sortOrder: sortOrderById.get(s.id) ?? s.sortOrder }))
            .sort((a, b) => a.sortOrder - b.sortOrder),
        );
        return { snapshot };
      },
      onError: (_err, _vars, onMutateResult) => {
        toast({ title: "Failed to save order", variant: "destructive" });
        if (onMutateResult?.snapshot) {
          queryClient.setQueryData<Status[]>(QUERY_KEY, onMutateResult.snapshot);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      },
    },
  });

  const statuses = serverStatuses ?? [];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<Status | null>(null);

  // Form state
  const [formKey, setFormKey] = useState("");
  const [formKeyTouched, setFormKeyTouched] = useState(false);
  const [formLabel, setFormLabel] = useState("");
  const [formAppliesToReports, setFormAppliesToReports] = useState(true);
  const [formAppliesToIssues, setFormAppliesToIssues] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const openCreate = () => {
    setEditingStatus(null);
    setFormKey("");
    setFormKeyTouched(false);
    setFormLabel("");
    setFormAppliesToReports(true);
    setFormAppliesToIssues(true);
    setIsDialogOpen(true);
  };

  const openEdit = (s: Status) => {
    setEditingStatus(s);
    setFormKey(s.key);
    setFormKeyTouched(true);
    setFormLabel(s.label);
    setFormAppliesToReports(s.appliesToReports);
    setFormAppliesToIssues(s.appliesToIssues);
    setIsDialogOpen(true);
  };

  const handleLabelChange = (value: string) => {
    setFormLabel(value);
    if (!editingStatus && !formKeyTouched) {
      setFormKey(slugify(value));
    }
  };

  const handleSubmit = () => {
    const key = formKey.trim();
    const label = formLabel.trim();
    if (!key || !label) {
      toast({ title: "Key and label are required", variant: "destructive" });
      return;
    }
    if (!formAppliesToReports && !formAppliesToIssues) {
      toast({ title: "Pick at least one scope (reports or issues)", variant: "destructive" });
      return;
    }

    const payload = {
      key,
      label,
      appliesToReports: formAppliesToReports,
      appliesToIssues: formAppliesToIssues,
    };

    const onConflictMessage = "A status with that key already exists";

    if (editingStatus) {
      updateStatus.mutate(
        { id: editingStatus.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: "Status updated" });
            setIsDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          },
          onError: (err: unknown) => {
            const msg =
              err instanceof ApiError && err.status === 409
                ? onConflictMessage
                : "Failed to update status";
            toast({ title: msg, variant: "destructive" });
          },
        },
      );
    } else {
      createStatus.mutate(
        { data: { ...payload, sortOrder: statuses.length } },
        {
          onSuccess: () => {
            toast({ title: "Status created" });
            setIsDialogOpen(false);
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
          },
          onError: (err: unknown) => {
            const msg =
              err instanceof ApiError && err.status === 409
                ? onConflictMessage
                : "Failed to create status";
            toast({ title: msg, variant: "destructive" });
          },
        },
      );
    }
  };

  const handleToggleActive = (s: Status) => {
    updateStatus.mutate(
      { id: s.id, data: { isActive: !s.isActive } },
      {
        onSuccess: () => {
          toast({
            title: s.isActive ? "Status deactivated" : "Status activated",
            description: s.isActive
              ? "It's hidden from new-record dropdowns. Existing records keep this status."
              : "It's available again in new-record dropdowns.",
          });
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: () => toast({ title: "Failed to update", variant: "destructive" }),
      },
    );
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      // One reorder at a time: a second drop while the first is saving would
      // take its snapshot from an order the server has not confirmed yet.
      if (reorderStatuses.isPending) return;

      const current = serverStatuses ?? [];
      const oldIndex = current.findIndex((s) => s.id === active.id);
      const newIndex = current.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(current, oldIndex, newIndex).map((s, i) => ({
        ...s,
        sortOrder: i,
      }));

      reorderStatuses.mutate({
        data: { items: reordered.map((s) => ({ id: s.id, sortOrder: s.sortOrder })) },
      });
    },
    [serverStatuses, reorderStatuses],
  );

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Statuses</h1>
          <p className="text-slate-500 text-sm">
            Statuses are admin-editable. Deactivate to hide from new-record dropdowns without
            affecting existing records. Drag to reorder.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-orange-600">
          <Plus className="w-4 h-4 mr-2" /> New Status
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : isError ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">
            Couldn't load statuses. Try refreshing the page.
          </CardContent>
        </Card>
      ) : statuses.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500 text-sm">
            No statuses yet. Click "New Status" to add one.
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4 text-slate-400" />
              All Statuses
              <Badge variant="secondary" className="ml-1">
                {statuses.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={statuses.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {statuses.map((s) => (
                      <SortableRow
                        key={s.id}
                        status={s}
                        onEdit={openEdit}
                        onToggleActive={handleToggleActive}
                        isToggling={updateStatus.isPending}
                      />
                    ))}
                  </TableBody>
                </SortableContext>
              </DndContext>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingStatus ? "Edit Status" : "New Status"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid gap-2">
              <Label htmlFor="status-label">Label</Label>
              <Input
                id="status-label"
                placeholder="e.g. Waiting on FMCSA"
                value={formLabel}
                onChange={(e) => handleLabelChange(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-slate-500">
                Shown in dropdowns and on email notifications.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="status-key">Key</Label>
              <Input
                id="status-key"
                placeholder="e.g. waiting_on_fmcsa"
                value={formKey}
                onChange={(e) => {
                  setFormKey(e.target.value);
                  setFormKeyTouched(true);
                }}
                className="font-mono"
                disabled={!!editingStatus}
              />
              <p className="text-xs text-slate-500">
                Lowercase letters, digits, and underscores. {editingStatus ? "Cannot be changed after creation." : "Auto-generated from label; click to customize."}
              </p>
            </div>

            <div className="grid gap-3">
              <Label>Applies to</Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scope-reports"
                  checked={formAppliesToReports}
                  onCheckedChange={(checked) => setFormAppliesToReports(checked === true)}
                />
                <Label htmlFor="scope-reports" className="font-normal cursor-pointer">
                  Bug reports
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="scope-issues"
                  checked={formAppliesToIssues}
                  onCheckedChange={(checked) => setFormAppliesToIssues(checked === true)}
                />
                <Label htmlFor="scope-issues" className="font-normal cursor-pointer">
                  Known issues
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={createStatus.isPending || updateStatus.isPending}
              className="bg-primary hover:bg-orange-600"
            >
              {editingStatus ? "Save Changes" : "Create Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffLayout>
  );
}
